import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "../api/client";
import { ProcessingStatusEnum, ProcessingStatusResponse, SOP } from "../types/sop";
import { useTrackChangesState } from "../store/useTrackChangesStore";
import { useSopStore } from "../store/useSopStore";
import { formatTime } from "../utils/time";

interface ProcessingStatus {
  isProcessing: boolean;
  status: ProcessingStatusEnum | "";
  progress: number;
  message: string;
  error: string | null;
  elapsedTime: number;
  formattedElapsedTime: string;
  processingTimeSeconds: number | null;
  formattedProcessingTime: string | null;
}

const useProcessingStatus = (sopId: string, selectedSOP?: SOP | null) => {
  const [status, setStatus] = useState<ProcessingStatus>({
    isProcessing: false,
    status: "",
    progress: 0,
    message: '',
    error: null,
    elapsedTime: 0,
    formattedElapsedTime: '00:00',
    processingTimeSeconds: null,
    formattedProcessingTime: null,
  });
  const { fetchSOPs, hasUnsavedChanges } = useTrackChangesState();
  const { updateSopProcessingStatus } = useSopStore();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const elapsedTimeIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const maxAttempts = 120;
  const intervalTimeInSeconds = 5;
  const attemptsRef = useRef(0);
  const hasCheckedInitialStatusRef = useRef<string | null>(null);

  // Check if we're on the correct SOP route
  const isOnSopRoute = useCallback(() => {
    const currentPath = window.location.pathname;
    const expectedBase = `/sop/${sopId}`;
    return (
      currentPath === expectedBase ||
      currentPath === `${expectedBase}/steps`
    );
  }, [sopId]);


  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (elapsedTimeIntervalRef.current) {
      clearInterval(elapsedTimeIntervalRef.current);
      elapsedTimeIntervalRef.current = null;
    }
  }, []);

  const poll = useCallback(async () => {
    if (!isOnSopRoute()) {
      console.log('useProcessingStatus: Not on SOP route, stopping polling');
      stopPolling();
      return;
    }

    console.log('🔄 Polling status for SOP:', sopId);
    try {
      const response = await api.get<ProcessingStatusResponse>(`/status/${sopId}`);
      const data = response.data;

      setStatus(prev => ({
        ...prev,
        isProcessing: true,
        status: data.status,
        error: data.error || null,
        processingTimeSeconds: data.processing_time_seconds || null,
        formattedProcessingTime: data.processing_time_seconds ? formatTime(data.processing_time_seconds) : null,
      }));

      switch (data.status) {
        case 'queued':
          setStatus(prev => ({
            ...prev,
            progress: 10,
            message: 'Job queued for processing...',
          }));
          break;

        case 'processing':
          setStatus(prev => ({
            ...prev,
            progress: Math.min(prev.progress + 5, 90),
            message: 'Processing your files with AI...',
          }));
          break;

        case 'regenerate':
          setStatus(prev => ({
            ...prev,
            progress: prev.progress > 0 ? prev.progress : 10,
            message: 'Updates requested',
            isProcessing: false,
          }));
          // No need to poll while waiting for admin review
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          if (elapsedTimeIntervalRef.current) {
            clearInterval(elapsedTimeIntervalRef.current);
            elapsedTimeIntervalRef.current = null;
          }
          return;

        case 'pending_verification':
          setStatus(prev => ({
            ...prev,
            progress: Math.min(prev.progress + 5, 90),
            message: 'Awaiting verification...',
            isProcessing: false,
          }));
          // Stop polling for pending verification - super admin can edit now
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          if (elapsedTimeIntervalRef.current) {
            clearInterval(elapsedTimeIntervalRef.current);
            elapsedTimeIntervalRef.current = null;
          }
          return;

        case 'done':
          setStatus(prev => ({
            ...prev,
            progress: 100,
            message: 'Processing complete!',
            isProcessing: false,
          }));

          // Refetch SOPs to get the latest version, but never overwrite local edits.
          if (!hasUnsavedChanges) {
            fetchSOPs();
          }
          // Stop polling directly to avoid circular dependency
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          if (elapsedTimeIntervalRef.current) {
            clearInterval(elapsedTimeIntervalRef.current);
            elapsedTimeIntervalRef.current = null;
          }
          return;

        case 'error': {
          let calculatedProcessingTime: number | undefined = undefined;
          if (data.processing_time_seconds && selectedSOP?.createdAt) {
            calculatedProcessingTime = data.processing_time_seconds;
          } else if (selectedSOP?.createdAt) {
            const now = Date.now();
            const sopCreatedTime = new Date(selectedSOP.createdAt + 'Z').getTime();
            calculatedProcessingTime = Math.floor((now - sopCreatedTime) / 1000);
          }

          updateSopProcessingStatus(sopId, ProcessingStatusEnum.ERROR, calculatedProcessingTime);

          setStatus(prev => ({
            ...prev,
            isProcessing: false,
            error: data.error || 'An error occurred during processing',
            message: 'Processing failed',
            processingTimeSeconds: calculatedProcessingTime || null,
            formattedProcessingTime: calculatedProcessingTime ? formatTime(calculatedProcessingTime) : null,
          }));
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          if (elapsedTimeIntervalRef.current) {
            clearInterval(elapsedTimeIntervalRef.current);
            elapsedTimeIntervalRef.current = null;
          }
          return;
        }
      }

      // Check timeout
      attemptsRef.current++;
      if (attemptsRef.current >= maxAttempts) {
        setStatus(prev => ({
          ...prev,
          isProcessing: false,
          error: 'Processing is taking longer than expected. Please try again later.',
          message: 'Timeout',
        }));
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        if (elapsedTimeIntervalRef.current) {
          clearInterval(elapsedTimeIntervalRef.current);
          elapsedTimeIntervalRef.current = null;
        }
      }

    } catch (error) {
      console.error('Error polling status:', error);
      setStatus(prev => ({
        ...prev,
        isProcessing: false,
        error: 'Failed to check processing status',
        message: 'Status check failed',
      }));
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (elapsedTimeIntervalRef.current) {
        clearInterval(elapsedTimeIntervalRef.current);
        elapsedTimeIntervalRef.current = null;
      }
    }
    }, [sopId, fetchSOPs, isOnSopRoute, stopPolling, updateSopProcessingStatus, selectedSOP]);

  const startElapsedTimeCounter = useCallback(() => {
    const now = Date.now();
    let startTime: number;
    
    if (selectedSOP?.createdAt) {
      const sopCreatedTime = new Date(selectedSOP.createdAt + 'Z').getTime();
      const elapsedFromCreation = Math.floor((now - sopCreatedTime) / 1000);

      if (elapsedFromCreation > 0) {
        startTime = sopCreatedTime;
        console.log('Using SOP createdAt as start time');
      } else {
        startTime = now;
        console.log('SOP createdAt appears to be in future, starting from now');
      }
    } else {
      startTime = now;
      console.log('No SOP createdAt, starting from now');
    }
    
    const updateElapsedTime = () => {
      const now = Date.now();
      const elapsedSeconds = Math.floor((now - startTime) / 1000);
      const formatted = formatTime(elapsedSeconds);
      
      setStatus(prev => ({
        ...prev,
        elapsedTime: elapsedSeconds,
        formattedElapsedTime: formatted,
      }));
    };

    updateElapsedTime();
    
    elapsedTimeIntervalRef.current = setInterval(updateElapsedTime, 1000);
  }, [selectedSOP?.createdAt]);

  const startPolling = useCallback(() => {
    if (!isOnSopRoute()) {
      console.log('useProcessingStatus: Not on SOP route, skipping polling start');
      return;
    }

    startElapsedTimeCounter();

    poll();
    intervalRef.current = setInterval(poll, intervalTimeInSeconds * 1000);
  }, [startElapsedTimeCounter, poll, isOnSopRoute]);


  useEffect(() => {
    if (!sopId || !selectedSOP) return;
    
    if (!isOnSopRoute()) {
      return;
    }
    
    // Reset the ref when sopId changes
    if (hasCheckedInitialStatusRef.current !== sopId) {
      hasCheckedInitialStatusRef.current = sopId;
    }

    // Check SOP data first - if it has a final status (done/error), use that
    if (selectedSOP.processingStatus === ProcessingStatusEnum.DONE) {
      setStatus({
        isProcessing: false,
        status: ProcessingStatusEnum.DONE,
        progress: 100,
        message: 'Processing complete!',
        error: null,
        elapsedTime: 0,
        formattedElapsedTime: '00:00',
        processingTimeSeconds: selectedSOP.processingTimeSeconds || null,
        formattedProcessingTime: selectedSOP.processingTimeSeconds ? formatTime(selectedSOP.processingTimeSeconds) : null,
      });
      return; // Don't start polling for done status
    }

    if (selectedSOP.processingStatus === ProcessingStatusEnum.ERROR) {
      setStatus({
        isProcessing: false,
        status: ProcessingStatusEnum.ERROR,
        progress: 0,
        message: 'Processing failed',
        error: 'Processing failed',
        elapsedTime: selectedSOP.processingTimeSeconds || 0,
        formattedElapsedTime: selectedSOP.processingTimeSeconds ? formatTime(selectedSOP.processingTimeSeconds) : '00:00',
        processingTimeSeconds: selectedSOP.processingTimeSeconds || null,
        formattedProcessingTime: selectedSOP.processingTimeSeconds ? formatTime(selectedSOP.processingTimeSeconds) : null,
      });
      return; // Don't start polling for error status
    }

    // If SOP has steps but no processing status, assume it's done
    if (selectedSOP?.getLatestVersion()?.steps?.length && selectedSOP.getLatestVersion()!.steps.length > 0) {
      setStatus({
        isProcessing: false,
        status: ProcessingStatusEnum.DONE,
        progress: 100,
        message: 'Processing complete!',
        error: null,
        elapsedTime: 0,
        formattedElapsedTime: '00:00',
        processingTimeSeconds: null,
        formattedProcessingTime: null,
      });
      return;
    }

    // Only start polling if status is not done/error (i.e., queued, processing, or undefined)
    startPolling();

    return () => {
      hasCheckedInitialStatusRef.current = null;
      stopPolling();
    };
  }, [sopId, selectedSOP, startPolling, stopPolling, isOnSopRoute]);


  return status
};

export default useProcessingStatus;